import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  FormGroup
} from '@angular/forms';
import { CanActivate, CanActivateChild, Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap, switchMap, map, finalize, shareReplay } from 'rxjs/operators';

export interface AuthDomainConfig {
  name: string;
  values: { url: string };
  isDefault?: boolean;
}

export interface AuthModuleConfig {
  domain: AuthDomainConfig;
  successLoginUrl?: string;
  successLogoutUrl?: string;
}

export const AUTH_MODULE_CONFIG = new InjectionToken<AuthModuleConfig>(
  'AUTH_MODULE_CONFIG'
);

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenKey = 'auth_token';
  private rememberKey = 'auth_remember_device';

  constructor(private router: Router) {}

  setToken(token: string, rememberDevice?: boolean): void {
    if (token) {
      localStorage.setItem(this.tokenKey, token);
      if (rememberDevice !== undefined) {
        localStorage.setItem(
          this.rememberKey,
          rememberDevice ? 'true' : 'false'
        );
      }
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.rememberKey);
    const reuseStrategy = this.router.routeReuseStrategy as any;
    if (reuseStrategy && typeof reuseStrategy.clearStoredRoutes === 'function') {
      reuseStrategy.clearStoredRoutes();
    }
    if (this.router.url !== '/login') {
      this.router.navigate(['/login']);
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  shouldRememberDevice(): boolean {
    return localStorage.getItem(this.rememberKey) === 'true';
  }
}

@Injectable({ providedIn: 'root' })
export class NeedAuthGuard implements CanActivate, CanActivateChild {
  constructor(private auth: AuthService, private router: Router) {}

  canActivate(): boolean {
    if (this.auth.isAuthenticated()) {
      return true;
    }
    this.router.navigate(['/login']);
    return false;
  }

  canActivateChild(): boolean {
    return this.canActivate();
  }
}

@Injectable()
export class HttpInterceptorProvider implements HttpInterceptor {
  private refreshInFlight$: Observable<string> | null = null;

  constructor(private auth: AuthService, private authApi: AuthApiService) {}

  private isPwaMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const isStandalone =
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches;
    const navStandalone = (window.navigator as any)?.standalone === true;
    return Boolean(isStandalone || navStandalone);
  }

  private readTokenFromResponse(res: any): string | null {
    return (
      res?.accessToken ||
      res?.token ||
      res?.access_token ||
      res?.access ||
      res?.data?.token ||
      null
    );
  }

  private refreshAccessToken(): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authApi.refresh().pipe(
        map((res: any) => {
          const newToken = this.readTokenFromResponse(res);
          if (!newToken) {
            throw new Error('AUTH_REFRESH_NO_TOKEN');
          }
          this.auth.setToken(newToken);
          return newToken;
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.refreshInFlight$;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Do not send token when calling auth endpoints
    const isAuthEndpoint =
      req.url.includes('/auth/login') ||
      req.url.includes('/auth/register') ||
      req.url.includes('/auth/recovery') ||
      req.url.includes('/auth/refresh');
    const retryHeader = 'x-refresh-retry';
    const alreadyRetried = req.headers.has(retryHeader);
    const token = this.auth.getToken();
    const request = token && !isAuthEndpoint
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        })
      : req;

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const payload = error?.error;
        const code = Array.isArray(payload) ? payload[0]?.code : payload?.code;
        const message = Array.isArray(payload) ? payload[0]?.message : payload?.message;
        const tokenExpired =
          error.status == 401 ||
          code === 'OTHW001' ||
          code === 'OTHFW003' ||
          /token.*caducad|token no existe/i.test(String(message || ''));

        if (!isAuthEndpoint && tokenExpired && this.auth.isAuthenticated()) {
          if (alreadyRetried) {
            this.auth.logout();
            return throwError(() => error);
          }

          return this.refreshAccessToken().pipe(
            switchMap((newToken: string) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`,
                  [retryHeader]: '1'
                }
              });
              return next.handle(retryReq);
            }),
            catchError(refreshErr => {
              this.auth.logout();
              return throwError(() => refreshErr);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  constructor(
    private http: HttpClient,
    @Inject(AUTH_MODULE_CONFIG) private cfg: AuthModuleConfig | null
  ) {}

  private baseUrl(): string {
    return this.cfg?.domain?.values?.url || '';
  }

  login(body: any) {
    const payload = {
      username: body?.email ?? body?.username ?? '',
      password: body?.password ?? '',
      rememberDevice: body?.rememberDevice === true,
      // cliente identificador para backend (según tabla account_client)
      client: 'test',
      // backend espera int, evitamos null
      pubK: body?.pubK ?? 1
    };
    return this.http.post(`${this.baseUrl()}/auth/login`, payload, { withCredentials: true });
  }

  register(body: any) {
    return this.http.post(`${this.baseUrl()}/auth/register`, body);
  }

  recovery(body: any) {
    return this.http.post(`${this.baseUrl()}/auth/recovery`, body);
  }

  refresh() {
    return this.http.post(`${this.baseUrl()}/auth/refresh`, {}, { withCredentials: true });
  }
}

import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  standalone: false,
  selector: 'app-auth',
  template: `
    <div class=\"login-hero\">
      <div class=\"login-backdrop\"></div>
      <div class=\"login-card\">
        <div class=\"login-card__brand\">
          <img
            src=\"assets/images/img-01.png\"
            alt=\"TBAGROSENSOR\"
            class=\"login-logo-img\"
          />
        </div>
        <div class=\"login-card__form\">
          <h2 class=\"login-title\">Acceso de usuario</h2>
          <form [formGroup]=\"form\" (ngSubmit)=\"submit()\" class=\"login-form\">
            <mat-form-field appearance=\"outline\" class=\"login-field\">
              <mat-label>Correo electrónico</mat-label>
              <mat-icon matPrefix>mail</mat-icon>
              <input
                matInput
                type=\"email\"
                formControlName=\"email\"
                autocomplete=\"username\"
                required
              />
            </mat-form-field>

            <mat-form-field appearance=\"outline\" class=\"login-field\">
              <mat-label>Contraseña</mat-label>
              <mat-icon matPrefix>lock</mat-icon>
              <input
                matInput
                type=\"password\"
                formControlName=\"password\"
                autocomplete=\"current-password\"
                required
              />
            </mat-form-field>

            <mat-checkbox
              color=\"primary\"
              formControlName=\"rememberDevice\"
              class=\"login-remember\"
            >
              Recordar en este dispositivo
            </mat-checkbox>

            <div class=\"login-error\" *ngIf=\"error\">{{ error }}</div>

            <button
              mat-flat-button
              color=\"primary\"
              class=\"login-submit\"
              type=\"submit\"
              [disabled]=\"form.invalid || loading\"
            >
              {{ loading ? 'Entrando...' : 'Entrar' }}
            </button>
          </form>
          <div class=\"login-links\">
            <button
              type=\"button\"
              class=\"login-link login-link--primary\"
              (click)=\"openRecoveryDialog()\"
            >
              ¿Olvidaste tu contraseña?
            </button>
            <button
              type=\"button\"
              class=\"login-link login-link--primary\"
              (click)=\"openRegisterDialog()\"
            >
              Crea tu cuenta
            </button>
          </div>
        </div>
      </div>
    </div>
  
  `
})


export class AuthComponent {
  form: FormGroup;
  loading = false;
  error: string = '';

  constructor(
    private fb: FormBuilder,
    private api: AuthApiService,
    private auth: AuthService,
    private router: Router,
    private dialog: MatDialog,
    @Inject(AUTH_MODULE_CONFIG) private cfg: AuthModuleConfig | null
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberDevice: [false]
    });
  }


  openRecoveryDialog() {
    this.dialog.open(RecoverDialogComponent, {
      width: '460px',
      maxWidth: '92vw',
      panelClass: 'register-dialog'
    });
  }

  openRegisterDialog() {
    this.dialog.open(RegisterDialogComponent, {
      width: '460px',
      maxWidth: '92vw',
      panelClass: 'register-dialog'
    });
  }

  submit() {
    if (this.form.invalid || this.loading) return;
    this.loading = true;
    this.error = '';
    const body = this.form.value;
    const rememberDevice = body?.rememberDevice === true;
    this.api
      .login(body)
      .pipe(
        tap((res: any) => {
          const token =
            res?.accessToken ||
            res?.token ||
            res?.access_token ||
            res?.access || // Highcharts backend devuelve {access, refresh}
            res?.data?.token;
          if (token) {
            this.auth.setToken(token, rememberDevice);
          }
          const redirect = this.cfg?.successLoginUrl || '/shell/home';
          const reuseStrategy = this.router.routeReuseStrategy as any;
          if (reuseStrategy && typeof reuseStrategy.clearStoredRoutes === 'function') {
            reuseStrategy.clearStoredRoutes();
          }
          this.router.navigateByUrl(redirect, { replaceUrl: true });
        }),
        catchError((err: any) => {
          const resolvedErr = typeof err === 'function' ? err() : err;
          const rawPayload = resolvedErr?.error ?? err?.error;
          let payload: any = rawPayload;
          if (typeof rawPayload === 'string') {
            try {
              payload = JSON.parse(rawPayload);
            } catch {
              payload = rawPayload;
            }
          }
          const first = Array.isArray(payload) ? payload[0] : payload;
          const payloadTextForCode =
            typeof payload === 'string'
              ? payload
              : JSON.stringify(payload || '');
          const code =
            first?.code ||
            ['EAUTH056', 'EAUTH057', 'EAUTH058', 'EAUTH064'].find(item =>
              payloadTextForCode.includes(item)
            ) ||
            null;
          const message =
            first?.message ||
            (typeof payload === 'string'
              ? payload
              : payload?.message || payload?.error || null);
          const fallbackText =
            resolvedErr?.message || resolvedErr?.statusText || null;
          const rawText =
            typeof rawPayload === 'string'
              ? rawPayload
              : JSON.stringify(rawPayload || '');
          const payloadText =
            typeof payload === 'string'
              ? payload
              : JSON.stringify(payload || '');
          const hasPending =
            code === 'EAUTH064' ||
            rawText.includes('EAUTH064') ||
            payloadText.includes('EAUTH064') ||
            /pendiente de verificaci/i.test(String(message || '')) ||
            /pendiente de verificaci/i.test(rawText) ||
            /pendiente de verificaci/i.test(payloadText) ||
            /pendiente de verificaci/i.test(String(fallbackText || ''));
          if (
            ['EAUTH056', 'EAUTH057', 'EAUTH058'].includes(code || '') &&
            message
          ) {
            this.error = String(message);
          } else if (hasPending) {
            this.error =
              'Tu cuenta está pendiente de verificación por un administrador.';
          } else {
            this.error = 'Login incorrecto';
          }
          return of(null);
        })


      )
      .subscribe({
        complete: () => (this.loading = false),
        error: () => (this.loading = false)
      });
  }
}

@Component({
  standalone: false,
  selector: 'app-recovery-dialog',
  template: `
    <h2 mat-dialog-title class="register-dialog__title">Recuperar contraseña</h2>
    <mat-dialog-content class="dialog-form register-dialog__content">
      <p class="register-dialog__hint">
        Introduce tu correo o usuario para recibir el enlace de recuperación.
      </p>
      <form
        id="recoveryForm"
        [formGroup]="form"
        (ngSubmit)="submit()"
        class="register-form-grid"
      >
        <mat-form-field appearance="outline" class="register-field">
          <mat-label>Correo o usuario</mat-label>
          <mat-icon matPrefix>mail</mat-icon>
          <input matInput type="text" formControlName="username" required />
        </mat-form-field>
      </form>
      <div class="register-error" *ngIf="error">{{ error }}</div>
      <div class="register-success" *ngIf="successMessage">{{ successMessage }}</div>
    </mat-dialog-content>
    <mat-dialog-actions class="register-actions">
      <button mat-button type="button" (click)="close()">Cancelar</button>
      <button
        mat-flat-button
        color="primary"
        type="submit"
        form="recoveryForm"
        [disabled]="loading || successMessage || !form.valid"
      >
        {{ loading ? 'Enviando...' : 'Enviar enlace' }}
      </button>
    </mat-dialog-actions>
  `
})
export class RecoverDialogComponent {
  form: FormGroup;
  loading = false;
  error = '';
  successMessage = '';

  constructor(
    private fb: FormBuilder,
    private api: AuthApiService,
    private dialogRef: MatDialogRef<RecoverDialogComponent>
  ) {
    this.form = this.fb.group({
      username: ['', Validators.required]
    });
  }

  submit() {
    if (this.form.invalid || this.loading || this.successMessage) return;
    this.loading = true;
    this.error = '';

    const payload = { username: this.form.value.username };

    this.api
      .recovery(payload)
      .pipe(
        tap(() => {
          this.successMessage =
            'Si la cuenta existe, recibirás un correo con el enlace de recuperación.';
          this.form.disable();
        }),
        catchError((err: any) => {
          const payload = err?.error;
          const code = Array.isArray(payload) ? payload[0]?.code : payload?.code;
          if (code === 'EAUTH080') {
            this.successMessage =
              'Si la cuenta existe, recibirás un correo con el enlace de recuperación.';
            this.form.disable();
          } else {
            this.error = 'No se pudo enviar el enlace de recuperación.';
          }
          return of(null);
        })
      )
      .subscribe({
        complete: () => (this.loading = false),
        error: () => (this.loading = false)
      });
  }

  close() {
    this.dialogRef.close();
  }
}

@Component({
  standalone: false,
  selector: 'app-register-dialog',
  template: `
    <h2 mat-dialog-title class=\"register-dialog__title\">Crear cuenta</h2>
    <mat-dialog-content class=\"dialog-form register-dialog__content\">
      <p class=\"register-dialog__hint\">
        Crea tu cuenta y espera la confirmación de un administrador.
      </p>
      <form
        id=\"registerForm\"
        [formGroup]=\"form\"
        (ngSubmit)=\"submit()\"
        class=\"register-form-grid\"
      >
        <mat-form-field appearance=\"outline\" class=\"register-field\">
          <mat-label>Correo electrónico</mat-label>
          <mat-icon matPrefix>mail</mat-icon>
          <input matInput type=\"email\" formControlName=\"email\" required />
        </mat-form-field>

        <mat-form-field appearance=\"outline\" class=\"register-field\">
          <mat-label>Contraseña</mat-label>
          <mat-icon matPrefix>lock</mat-icon>
          <input matInput type=\"password\" formControlName=\"password\" required />
          <mat-error *ngIf=\"form.get('password')?.invalid\">La contraseña debe tener al menos 8 caracteres.</mat-error>
        </mat-form-field>

        <mat-checkbox formControlName=\"isAcceptedTerms\" class=\"register-terms\">
          Acepto los términos y condiciones
        </mat-checkbox>
      </form>
      <div class=\"register-error\" *ngIf=\"error\">{{ error }}</div>
      <div class=\"register-success\" *ngIf=\"successMessage\">{{ successMessage }}</div>
    </mat-dialog-content>
    <mat-dialog-actions class=\"register-actions\">
      <button mat-button type=\"button\" (click)=\"close()\">Cancelar</button>
      <button
        mat-flat-button
        color=\"primary\"
        type=\"submit\"
        form=\"registerForm\"
        [disabled]='loading || successMessage || !form.get("email")?.valid || !form.get("password")?.valid || !form.get("isAcceptedTerms")?.value'
      >
        {{ loading ? 'Enviando...' : 'Crear cuenta' }}
      </button>
    </mat-dialog-actions>
  `
})
export class RegisterDialogComponent {
  form: FormGroup;
  loading = false;
  error = '';
  successMessage = '';

  constructor(
    private fb: FormBuilder,
    private api: AuthApiService,
    private dialogRef: MatDialogRef<RegisterDialogComponent>
  ) {
    this.form = this.fb.group({      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      isAcceptedTerms: [false, Validators.requiredTrue]
    });
  }

  submit() {
    if (this.form.invalid || this.loading || this.successMessage) return;
    this.loading = true;
    this.error = '';
    const payload = {
      username: this.form.value.email,
      email: this.form.value.email,
      password: this.form.value.password,
      isAcceptedTerms: this.form.value.isAcceptedTerms ? 1 : 0
    };

    this.api
      .register(payload)
      .pipe(
        tap(() => {
          this.successMessage =
            'Solicitud enviada. Revisa tu correo para activar la cuenta.';
          this.form.disable();
        }),
        catchError((err: any) => {
          const payload = err?.error;
          const message =
            (Array.isArray(payload) ? payload[0]?.message : payload?.message) ||
            payload?.error ||
            null;
          this.error = message || 'No se pudo crear la cuenta.';
          return of(null);
        })
      )
      .subscribe({
        complete: () => (this.loading = false),
        error: () => (this.loading = false)
      });
  }

  close() {
    this.dialogRef.close();
  }
}

@NgModule({
  declarations: [AuthComponent, RecoverDialogComponent, RegisterDialogComponent],
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  exports: [AuthComponent]
})
export class AuthModule {
  static forRoot(config: AuthModuleConfig): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [{ provide: AUTH_MODULE_CONFIG, useValue: config }]
    };
  }
}
